import { ReactElement, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import FormCard from '../../components/FormCard';
import SectionHeader from '../../components/SectionHeader';
import Spinner from '../../components/Spinner';
import Table from '../../components/Table';
import { useEpisodeList } from '../../hooks/useEpisodeDbCache';
import { useTeamEpisodeFiles } from '../../hooks/useEpisodeDbScan';
import { useUser } from '../../hooks/useUser';
import { isAdmin } from '../../utils/userRole';

/**
 * Account → Recordings: the Nimbus recording files (.db) a phone has
 * uploaded. Everyone sees their own team's. An admin can also list every
 * team's — the reach the admin episode viewer had before it moved here —
 * and that list alone knows which files are already in the server's cache.
 */
interface RecordingRow {
  s3_key: string;
  filename: string;
  captured_ts: string;
  device_id: string | null;
  // Only the all-teams list reports it; undefined on the team list.
  cached?: boolean;
}

function toIsoDateString(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function defaultFrom(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 7);
  return toIsoDateString(d);
}

function defaultTo(): string {
  return toIsoDateString(new Date());
}

function isoBound(date: string, end: boolean): string {
  return end ? `${date}T23:59:59Z` : `${date}T00:00:00Z`;
}

export default function EpisodesListView(): ReactElement {
  const [userProfile] = useUser();
  const admin = isAdmin(userProfile);
  const [from, setFrom] = useState<string>(defaultFrom());
  const [to, setTo] = useState<string>(defaultTo());
  // Admins start where the admin viewer always started: every team's files.
  const [allTeams, setAllTeams] = useState<boolean>(admin);
  const navigate = useNavigate();

  const everyTeam = admin && allTeams;
  const fromIso = isoBound(from, false);
  const toIso = isoBound(to, true);
  const adminList = useEpisodeList(fromIso, toIso, everyTeam);
  const teamList = useTeamEpisodeFiles(fromIso, toIso, !everyTeam);
  const { data, isPending, isError, error, refetch, isFetching } = everyTeam ? adminList : teamList;

  const columns = useMemo<ColumnDef<RecordingRow, unknown>[]>(
    () => [
      ...(everyTeam
        ? [
          {
            accessorKey: 'cached',
            header: '',
            cell: ({ row }) => (row.original.cached ? '' : '★'),
            size: 24,
          } as ColumnDef<RecordingRow, unknown>,
        ]
        : []),
      {
        accessorKey: 'captured_ts',
        header: 'Captured (UTC)',
        cell: ({ row }) => row.original.captured_ts.replace('T', ' ').slice(0, 19),
      },
      { accessorKey: 'filename', header: 'Filename' },
      { accessorKey: 'device_id', header: 'Device ID' },
    ],
    [everyTeam],
  );

  const handleRowClick = (_e: React.MouseEvent, row: RecordingRow) => {
    navigate(`/account/episodes/detail/${row.s3_key}`);
  };

  return (
    <>
      <SectionHeader
        title="Recordings"
        isLoading={isFetching}
        backLink={{ to: '/account', text: 'Back to account' }}
      />

      <FormCard>
        <div className="cr-flex cr-flex-col cr-gap-3 cr-p-3 md:cr-flex-row md:cr-items-end">
          <label className="cr-flex cr-flex-col">
            <span className="cr-text-xs cr-font-medium cr-text-gray-500">From</span>
            <input
              type="date"
              className="cr-mt-1 cr-rounded-md cr-border cr-border-gray-300 cr-px-3 cr-py-2 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          <label className="cr-flex cr-flex-col">
            <span className="cr-text-xs cr-font-medium cr-text-gray-500">To</span>
            <input
              type="date"
              className="cr-mt-1 cr-rounded-md cr-border cr-border-gray-300 cr-px-3 cr-py-2 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
          <button
            className="cr-rounded-md cr-bg-blue-600 cr-px-3 cr-py-2 cr-text-sm cr-font-medium cr-text-white hover:cr-bg-blue-700"
            onClick={() => refetch()}
          >
            Refresh
          </button>
          {admin && (
            <label className="cr-flex cr-items-center cr-gap-2 cr-whitespace-nowrap cr-pb-2 cr-text-sm cr-text-gray-700">
              <input
                type="checkbox"
                checked={allTeams}
                onChange={(e) => setAllTeams(e.target.checked)}
                className="cr-cursor-pointer"
              />
              All teams
            </label>
          )}
        </div>
        <p className="cr-px-3 cr-pb-2 cr-text-left cr-text-xs cr-text-gray-500">
          {everyTeam
            ? 'Every recording file in the bucket, whichever team it belongs to. ★ = not yet cached on the server.'
            : 'Recording files uploaded from your team’s phones. Open one to see which of its runs are on the server.'}
        </p>
      </FormCard>

      {isPending && <Spinner isFetching />}

      {isError && (
        <p className="cr-mt-2 cr-text-sm cr-text-red-600">
          Failed to load recordings: {(error as Error)?.message ?? 'unknown error'}
        </p>
      )}

      {!isPending && !isError && (
        (data ?? []).length > 0 ? (
          <Table<RecordingRow>
            data={data ?? []}
            columns={columns}
            onRowClick={handleRowClick}
            getRowId={(row) => row.s3_key}
          />
        ) : (
          <p className="cr-mt-2 cr-text-sm cr-text-gray-500">No recordings uploaded in this period.</p>
        )
      )}
    </>
  );
}
